/**
 * Optional account: a recovery code shown once, a pairing code for a second device,
 * and an honest status for history sync. Sitting does not come through here.
 */

import { useEffect, useState } from 'preact/hooks';
import {
  accountSession,
  createAccount,
  deleteAccount,
  issuePairing,
  recoverAccount,
  redeemPairing,
  rotateRecovery,
  signOutThisDevice,
  setPendingRecovery,
  pendingRecovery,
  AuthError,
} from '../account/session';
import { syncHistory, syncStatus, type SyncStatus } from '../account/sync';

interface Props { onBack: () => void }

type View =
  | { kind: 'main' }
  | { kind: 'pair-show'; code: string; expiresAt: number }
  | { kind: 'pair-enter' }
  | { kind: 'recover-enter' }
  | { kind: 'delete-confirm' };

/**
 * Shown with the code, once. Email recovery does not exist yet; anyone holding
 * this code holds the account; losing it with this device loses the history.
 * Do not soften this, and do not call the account a backup.
 */
export const RECOVERY_LOSS_COPY =
  'This recovery code is the only way back onto this account. Email recovery is not available yet. Anyone who has this code can use the account. If you lose this code and you lose this device, the listening history on this account is gone. We cannot get it back for you.';

export const ROTATE_COPY =
  'The previous recovery code stops working immediately. Other devices stay signed in.';

export const SIGN_OUT_COPY =
  'Signs this device out. Other devices stay signed in.';

export const DELETE_COPY =
  'This permanently deletes the account, the recovery code, every signed-in session, and the listening history stored on the account. Sits already on this phone stay on this phone. This cannot be undone.';

export function Account({ onBack }: Props) {
  const session = accountSession.value;
  const pending = pendingRecovery.value;
  const status = syncStatus.value;
  const [view, setView] = useState<View>({ kind: 'main' });
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const [pairCode, setPairCode] = useState('');
  const [recoveryInput, setRecoveryInput] = useState('');
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (session) void syncHistory();
  }, [session]);

  useEffect(() => {
    if (view.kind !== 'pair-show') return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [view]);

  const showingRecovery = pending !== null;

  const back = () => {
    // This used to refuse while the recovery code was up, and the button rendered
    // disabled — a dead control with no explanation, which reads as a broken app rather
    // than as a safeguard. It was never protecting anything either: `setPendingRecovery`
    // writes the code to localStorage, so leaving keeps it and the panel comes back.
    // The acknowledgement still gates the one action that discards it for good.
    if (view.kind !== 'main') {
      setView({ kind: 'main' });
      setError(null);
      return;
    }
    onBack();
  };

  const run = async (action: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof AuthError ? err.message : 'The request could not be completed.');
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async (code: string) => {
    // The code is shown once, so a silent failure is the dangerous outcome: the person
    // walks away believing it is on the clipboard. Report the denial and leave the state
    // up rather than clearing it on a timer.
    try {
      await navigator.clipboard.writeText(code);
      setCopyState('copied');
      setTimeout(() => setCopyState((was) => (was === 'copied' ? 'idle' : was)), 2500);
    } catch {
      setCopyState('failed');
    }
  };

  const acknowledge = () => {
    if (!acknowledged) return;
    setPendingRecovery(null);
    setAcknowledged(false);
    setCopyState('idle');
    setView({ kind: 'main' });
  };

  return (
    <div class="screen">
      <div class="shell stack" style={{ flex: 1 }}>
        <button class="btn btn-quiet" onClick={back}
                style={{ alignSelf: 'flex-start', minHeight: '2.5rem', paddingLeft: 0 }}>
          ← Back
        </button>

        {showingRecovery ? (
          <RecoveryPanel
            code={pending.code}
            rotated={pending.rotated}
            acknowledged={acknowledged}
            copyState={copyState}
            busy={busy}
            onAckChange={setAcknowledged}
            onCopy={() => void copyCode(pending.code)}
            onContinue={acknowledge}
          />
        ) : view.kind === 'pair-show' ? (
          <PairShow
            code={view.code}
            expiresAt={view.expiresAt}
            now={now}
            busy={busy}
            copyState={copyState}
            onCopy={() => void copyCode(view.code)}
            onAnother={() => void run(async () => {
              const issued = await issuePairing();
              setNow(Date.now());
              setView({ kind: 'pair-show', code: issued.pairingCode, expiresAt: issued.expiresAt });
            })}
          />
        ) : view.kind === 'pair-enter' ? (
          <CodeForm
            title="I have a code"
            hint="Type the short code from the signed-in device. It lasts five minutes and works once."
            label="Pairing code"
            value={pairCode}
            onInput={setPairCode}
            submitLabel="Use this code"
            busy={busy}
            onSubmit={() => void run(async () => {
              await redeemPairing(pairCode);
              setPairCode('');
              setView({ kind: 'main' });
            })}
          />
        ) : view.kind === 'recover-enter' ? (
          <CodeForm
            title="I have a recovery code"
            hint="Type the recovery code that was shown when this account was created, or when it was last replaced."
            label="Recovery code"
            value={recoveryInput}
            onInput={setRecoveryInput}
            submitLabel="Use this code"
            busy={busy}
            onSubmit={() => void run(async () => {
              await recoverAccount(recoveryInput);
              setRecoveryInput('');
              setView({ kind: 'main' });
            })}
          />
        ) : view.kind === 'delete-confirm' ? (
          <DeleteConfirm
            busy={busy}
            onCancel={() => setView({ kind: 'main' })}
            onDelete={() => void run(async () => {
              await deleteAccount();
              setView({ kind: 'main' });
            })}
          />
        ) : session ? (
          <SignedIn
            username={session.user.username}
            status={status}
            busy={busy}
            onSync={() => void run(async () => { await syncHistory(); })}
            onPair={() => void run(async () => {
              const issued = await issuePairing();
              setNow(Date.now());
              setView({ kind: 'pair-show', code: issued.pairingCode, expiresAt: issued.expiresAt });
            })}
            onRotate={() => void run(async () => {
              setAcknowledged(false);
              await rotateRecovery();
            })}
            onSignOut={() => void run(async () => { await signOutThisDevice(); })}
            onDelete={() => setView({ kind: 'delete-confirm' })}
          />
        ) : (
          <SignedOut
            busy={busy}
            onCreate={() => void run(async () => {
              setAcknowledged(false);
              await createAccount();
            })}
            onPairEnter={() => { setError(null); setView({ kind: 'pair-enter' }); }}
            onRecoverEnter={() => { setError(null); setView({ kind: 'recover-enter' }); }}
          />
        )}

        {error && (
          <p role="alert" class="account-warning">{error}</p>
        )}
      </div>
    </div>
  );
}

function SignedOut({ busy, onCreate, onPairEnter, onRecoverEnter }: {
  busy: boolean;
  onCreate: () => void;
  onPairEnter: () => void;
  onRecoverEnter: () => void;
}) {
  return (
    <>
      <h1>Account</h1>
      <p>
        Sitting never needs an account, and nothing here is required to practise.
        An account is a way to use this history on another device. After you create
        one, you will see a recovery code once. That code is the only way back if
        this device is lost. Email recovery is not available yet. If you lose the
        code and you lose this device, the history on the account is gone.
      </p>
      <button class="btn btn-primary" disabled={busy} onClick={onCreate}>
        Create an account
      </button>
      <button class="btn" disabled={busy} onClick={onPairEnter}>
        I have a code
      </button>
      <button class="btn" disabled={busy} onClick={onRecoverEnter}>
        I have a recovery code
      </button>
    </>
  );
}

function SignedIn({ username, status, busy, onSync, onPair, onRotate, onSignOut, onDelete }: {
  username: string;
  status: SyncStatus;
  busy: boolean;
  onSync: () => void;
  onPair: () => void;
  onRotate: () => void;
  onSignOut: () => void;
  onDelete: () => void;
}) {
  return (
    <>
      <h1>Account</h1>
      <p>
        Signed in as <span class="fig">{username}</span>. Sitting does not depend on this.
      </p>
      <p class="dim" data-testid="sync-status" role="status">
        {statusLine(status)}
      </p>
      <button class="btn" disabled={busy} onClick={onSync}>Sync now</button>
      <hr />
      <section class="stack-sm">
        <h3 class="eyebrow">Another device</h3>
        <p class="dim" style={{ fontSize: 'var(--step--1)' }}>
          Shows a short code on this phone. Type it on the other device within five minutes.
        </p>
        <button class="btn" disabled={busy} onClick={onPair}>Add another device</button>
      </section>
      <hr />
      <section class="stack-sm">
        <h3 class="eyebrow">Recovery code</h3>
        <p class="dim" style={{ fontSize: 'var(--step--1)' }}>
          Replace the current recovery code. The previous one stops working immediately.
          Other devices stay signed in.
        </p>
        <button class="btn" disabled={busy} onClick={onRotate}>Get a new recovery code</button>
      </section>
      <hr />
      <section class="stack-sm">
        <h3 class="eyebrow">This device</h3>
        <p class="dim" style={{ fontSize: 'var(--step--1)' }}>{SIGN_OUT_COPY}</p>
        <button class="btn" disabled={busy} onClick={onSignOut}>Sign out</button>
      </section>
      <hr />
      <section class="stack-sm">
        <h3 class="eyebrow">Delete</h3>
        <button class="btn" disabled={busy} onClick={onDelete}
                style={{ alignSelf: 'flex-start', color: 'var(--warn)' }}>
          Delete account
        </button>
      </section>
    </>
  );
}

type CopyState = 'idle' | 'copied' | 'failed';

const COPY_HINT: Record<CopyState, string> = {
  idle: 'Tap the code to copy it.',
  copied: 'Copied to the clipboard.',
  failed: 'The clipboard refused. Write the code down instead.',
};

/**
 * The code, as a button.
 *
 * Selectable text was the previous answer and it is the wrong one on a phone: a long
 * press raises the system selection handles and Copy bubble, which is the clearest
 * single tell that an installed app is a web page. `styles.css` turns selection off for
 * the whole body for that reason, and this used to opt back in.
 */
function CodeButton({ code, state, onCopy, testId }: {
  code: string;
  state: CopyState;
  onCopy: () => void;
  testId: string;
}) {
  return (
    <div class="account-code-row">
      <button type="button" class="account-code fig" data-testid={testId} onClick={onCopy}
              aria-label={`Copy the code ${code}`}>
        {code}
      </button>
      <p class="account-code-hint" data-state={state} role="status" data-testid={`${testId}-hint`}>
        {COPY_HINT[state]}
      </p>
    </div>
  );
}

function RecoveryPanel({ code, rotated, acknowledged, copyState, busy, onAckChange, onCopy, onContinue }: {
  code: string;
  rotated: boolean;
  acknowledged: boolean;
  copyState: CopyState;
  busy: boolean;
  onAckChange: (value: boolean) => void;
  onCopy: () => void;
  onContinue: () => void;
}) {
  return (
    <>
      <h1>{rotated ? 'New recovery code' : 'Recovery code'}</h1>
      {rotated && <p>{ROTATE_COPY}</p>}
      <p class="account-warning">{RECOVERY_LOSS_COPY}</p>
      <CodeButton code={code} state={copyState} onCopy={onCopy} testId="recovery-code" />
      <p class="account-code-hint">
        Going back keeps this code. It stays on this screen until you confirm below.
      </p>
      <label class="spread" style={{ cursor: 'pointer', alignItems: 'flex-start' }}>
        <span>
          I have saved this code. I understand that losing it, with no other signed-in
          device, loses the history on this account.
        </span>
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => onAckChange((e.target as HTMLInputElement).checked)}
          style={{ width: '1.4rem', height: '1.4rem', accentColor: 'var(--accent-fill)',
                   marginTop: '0.2rem', flexShrink: 0 }}
        />
      </label>
      <button class="btn btn-primary" disabled={!acknowledged || busy} onClick={onContinue}>
        I have saved this code
      </button>
    </>
  );
}

function PairShow({ code, expiresAt, now, busy, copyState, onCopy, onAnother }: {
  code: string;
  expiresAt: number;
  now: number;
  busy: boolean;
  copyState: CopyState;
  onCopy: () => void;
  onAnother: () => void;
}) {
  const remaining = expiresAt - now;
  const expired = remaining <= 0;
  return (
    <>
      <h1>Add another device</h1>
      <p>
        Type this code on the other device. It works once, and only for five minutes.
        Issuing a new code makes this one stop working.
      </p>
      <CodeButton code={code} state={copyState} onCopy={onCopy} testId="pairing-code" />
      <p class="fig" data-testid="pairing-countdown">
        {expired ? 'This code has expired.' : `Expires in ${formatRemaining(remaining)}`}
      </p>
      <button class="btn" disabled={busy} onClick={onAnother}>Get a new code</button>
    </>
  );
}

function CodeForm({ title, hint, label, value, onInput, submitLabel, busy, onSubmit }: {
  title: string;
  hint: string;
  label: string;
  value: string;
  onInput: (value: string) => void;
  submitLabel: string;
  busy: boolean;
  onSubmit: () => void;
}) {
  return (
    <>
      <h1>{title}</h1>
      <p class="dim">{hint}</p>
      <form class="stack-sm" onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
        <label class="stack-sm">
          <span class="eyebrow">{label}</span>
          <input
            class="account-input"
            value={value}
            autoComplete="off"
            autoCorrect="off"
            spellcheck={false}
            autoCapitalize="characters"
            onInput={(e) => onInput((e.target as HTMLInputElement).value)}
          />
        </label>
        <button class="btn btn-primary" disabled={busy || value.trim().length === 0} type="submit">
          {submitLabel}
        </button>
      </form>
    </>
  );
}

function DeleteConfirm({ busy, onCancel, onDelete }: {
  busy: boolean;
  onCancel: () => void;
  onDelete: () => void;
}) {
  return (
    <>
      <h1>Delete account</h1>
      <p class="account-warning">{DELETE_COPY}</p>
      <button class="btn" disabled={busy} onClick={onCancel}>Cancel</button>
      <button class="btn btn-primary" disabled={busy} onClick={onDelete}
              style={{ background: 'var(--btn-fill)' }}>
        Delete this account
      </button>
    </>
  );
}

function statusLine(status: typeof syncStatus.value): string {
  if (status.kind === 'idle') return 'History on this phone stays on this phone.';
  if (status.kind === 'pending') return 'Waiting to send this phone’s history.';
  if (status.kind === 'synced') return 'History is in sync.';
  return status.reason;
}

function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
